import { t } from '@hefe/i18n';
import {
  SelectOption,
  User,
  UserProgress,
  UserTermination,
} from '@hefe/models';
import { formatDate } from '@hefe/utils';
import {
  createAsyncThunk,
  createSelector,
  createSlice,
  PayloadAction,
} from '@reduxjs/toolkit';
import API from '../../../api/api';
import { BasketballUserSaveRequest } from '../../../models/requests/basketball-user';
import { isCacheValid } from '../../../utils/cache/caching';
import {
  activateUsers,
  createUserTermination,
  deleteMultiUsers,
  deleteUserTermination,
  updateUserChildren,
  updateUserRights,
} from '../../base/users/users.slice';
import { cacheActions } from '../../system/cache/cache.slice';

const FEATURE_KEY = 'basketballUsers';

/************************************************************************************
 *
 *                                  ACTIONS
 *
 ************************************************************************************
 */
export const fetchBasketballUsers = createAsyncThunk(
  'basketballUsers/getAll',
  async (thunkAPI) => {
    const response = await API.basketballUsers().getAll();
    return response?.data.data;
  }
);

export const fetchBasketballUsersSelect = createAsyncThunk(
  'basketballUsers/getAllSelect',
  async (data: { cacheDuration: number }, thunkAPI) => {
    if (!isCacheValid('basketballUsers/getAllSelect', data, thunkAPI)) {
      const response = await API.basketballUsers().getAllSelect();
      return response?.data;
    }
  }
);

export const createBasketballUser = createAsyncThunk(
  'basketballUsers/create',
  async (data: BasketballUserSaveRequest, thunkAPI) => {
    const response = await API.members().create({
      ...data,
      basketball: true,
    });

    if (response?.data) {
      thunkAPI.dispatch(
        cacheActions.clearCache('basketballUsers/getAllSelect')
      );
    }

    return response?.data;
  }
);

export const updateBasketballUser = createAsyncThunk(
  'basketballUsers/update',
  async (data: BasketballUserSaveRequest, thunkAPI) => {
    const response = await API.members().update({
      ...data,
      basketball: true,
    });
    return response?.data;
  }
);

/************************************************************************************
 *
 *                                      SLICE
 *
 ************************************************************************************
 */

interface BasketballUserSelectOption extends SelectOption {
  birthday?: string;
}

interface BasketballUserEmailAddress {
  id: number;
  user_id: number;
  email: string;
  receive_notifications: boolean;
  label?: string;
  created_at?: string;
  updated_at?: string;
}

const emailAddressesSignature = (
  emailAddresses?: BasketballUserEmailAddress[]
): string => {
  if (!emailAddresses || emailAddresses.length === 0) {
    return '';
  }

  return emailAddresses
    .map(
      (item) =>
        `${item.id}|${item.user_id}|${item.email}|${item.label || ''}|${
          item.receive_notifications ? '1' : '0'
        }|${item.updated_at || ''}`
    )
    .sort()
    .join('||');
};

export interface BasketballUsersState {
  users: User[];
  usersSelect: BasketballUserSelectOption[];
  loading: boolean;
}

const initialState: BasketballUsersState = {
  users: [],
  usersSelect: [],
  loading: false,
};

export const basketballUsersSlice = createSlice({
  name: FEATURE_KEY,
  initialState: initialState,
  reducers: {
    setUserEmailAddresses: (
      state: BasketballUsersState,
      action: PayloadAction<{
        userId: number;
        emailAddresses: BasketballUserEmailAddress[];
      }>
    ) => {
      const user = state.users.find((u) => u.id === action.payload.userId);

      if (!user) {
        return;
      }

      const currentEmailAddresses = (
        user as User & { email_addresses?: BasketballUserEmailAddress[] }
      ).email_addresses;

      if (
        emailAddressesSignature(currentEmailAddresses) ===
        emailAddressesSignature(action.payload.emailAddresses)
      ) {
        return;
      }

      // Keep loaded email-addresses on the user profile so other tabs/components can read them.
      (
        user as User & { email_addresses?: BasketballUserEmailAddress[] }
      ).email_addresses = action.payload.emailAddresses;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(
      fetchBasketballUsers.pending,
      (state: BasketballUsersState) => {
        state.loading = true;
      }
    );
    builder.addCase(
      fetchBasketballUsers.rejected,
      (state: BasketballUsersState) => {
        state.loading = false;
      }
    );
    builder.addCase(
      fetchBasketballUsers.fulfilled,
      (state: BasketballUsersState, action: PayloadAction<User[]>) => {
        state.users = action.payload;
        state.loading = false;

        state.usersSelect = state.users.map(
          (user) =>
            ({
              label: `${user.firstname} ${user.lastname} ${
                user.birthday ? `(${formatDate(user.birthday)})` : ''
              }`,
              value: user.id,
            } as SelectOption)
        );
      }
    );
    builder.addCase(
      fetchBasketballUsersSelect.fulfilled,
      (
        state: BasketballUsersState,
        action: PayloadAction<BasketballUserSelectOption[]>
      ) => {
        if (action.payload) {
          state.usersSelect = action.payload;
        }
      }
    );
    builder.addCase(
      createBasketballUser.fulfilled,
      (state: BasketballUsersState, action: PayloadAction<User>) => {
        const user = action.payload;

        state.users.push(user);
        state.users.sort((a: User, b: User) =>
          a.lastname.localeCompare(b.lastname)
        );

        state.usersSelect.push({
          label: `${user.firstname} ${user.lastname} ${
            user.birthday ? `(${formatDate(user.birthday)})` : ''
          }`,
          value: user.id,
        });
      }
    );
    builder.addCase(
      updateBasketballUser.fulfilled,
      (state: BasketballUsersState, action: PayloadAction<User>) => {
        const user = state.users.find((user) => user.id === action.payload.id);
        if (user) {
          state.users = state.users.map((user) =>
            user.id === action.payload.id ? action.payload : user
          );
        }
      }
    );
    /******************************************************************************************
     *                              Generic user actions
     *****************************************************************************************/
    builder.addCase(
      deleteMultiUsers.fulfilled,
      (state: BasketballUsersState, action: PayloadAction<number[]>) => {
        state.users = state.users.filter(
          (user) => !action.payload.includes(user.id)
        );
      }
    );
    builder.addCase(
      activateUsers.fulfilled,
      (state: BasketballUsersState, action: PayloadAction<number[]>) => {
        state.users.forEach((user) => {
          if (action.payload.includes(user.id)) {
            user.in_progress = UserProgress.ACTIVE;
          }
        });
      }
    );
    builder.addCase(
      updateUserRights.fulfilled,
      (state: BasketballUsersState, action: PayloadAction<User>) => {
        state.users = state.users.map((user) =>
          user.id === action.payload.id ? action.payload : user
        );
      }
    );
    builder.addCase(
      updateUserChildren.fulfilled,
      (state: BasketballUsersState, action: PayloadAction<User>) => {
        state.users = state.users.map((user) =>
          user.id === action.payload.id ? action.payload : user
        );
      }
    );
    builder.addCase(
      createUserTermination.fulfilled,
      (state: BasketballUsersState, action: PayloadAction<UserTermination>) => {
        const user = state.users.find(
          (user) => user.id === action.payload.user_id
        );
        if (user) {
          user.active_termination = action.payload;
        }
      }
    );
    builder.addCase(
      deleteUserTermination.fulfilled,
      (state: BasketballUsersState, action: PayloadAction<UserTermination>) => {
        const user = state.users.find(
          (user) => user.id === action.payload.user_id
        );
        if (user) {
          user.active_termination = undefined;
        }
      }
    );
  },
});

/*
 * Export reducer for store configuration.
 */
export const basketballUsersReducer = basketballUsersSlice.reducer;
export const basketballUsersActions = basketballUsersSlice.actions;

const state = (rootState: any): BasketballUsersState => rootState[FEATURE_KEY];

export const basketballUsersSelector = createSelector(state, (state) => {
  return state.users;
});

export const basketballUsersSelectSelector = createSelector(state, (state) => {
  return state.usersSelect;
});

export const basketballUsersSelectWithBirthdaySelector = createSelector(
  state,
  (state) => {
    return state.usersSelect.map((user) => {
      const birthday = user.birthday ? `(${formatDate(user.birthday)})` : '';
      return {
        value: user.value,
        label: `${user.label} ${birthday}`,
      };
    });
  }
);

export const basketballUsersSelectWithBirthdayWithPrefixSelector =
  createSelector(state, (state) => {
    return state.usersSelect.map((user) => {
      const birthday = user.birthday ? `(${formatDate(user.birthday)})` : '';
      return {
        value: 'u_' + user.value,
        label: `${user.label} ${birthday}`,
      };
    });
  });

export const basketballUsersSelectWithPendingSelector = createSelector(
  state,
  (state) => {
    const pendingUser: BasketballUserSelectOption = {
      label: t('pending'),
      value: null,
    };
    return [pendingUser, ...state.usersSelect];
  }
);

export const basketballUsersLoadingSelector = createSelector(state, (state) => {
  return state.loading;
});
